import { connect } from "react-redux";
import { useEffect, useRef, useState } from "react";
import close_icon from "../../../../assets/icons/close.svg";
import Formulario from "../../../components/Formulario";
import Loading from "../../../components/Loading";
import AlertDialog from "../../../components/AlertDialog";
import CodigoEmail from "../../../components/CodigoEmail";
import { mapDispatchToProps, mapStateToProps } from "../../../../utils/config";
import { emailValido } from "../../../../utils/trocaEmail";
import ControllerConfiguracoes from "../Home/controller";

const ESPERA_REENVIO = 60; // segundos antes de poder pedir outro código

// O visual de .resend-code (herdado dos modais de conta) é de um botão minúsculo de 170px.
const SECUNDARIO = { maxWidth: "none", width: "100%", height: "auto", minHeight: 34, padding: "8px 10px", fontSize: 12, marginTop: 10 };

const CAIXA = {
  erro: { color: "#C43333", background: "transparent", border: "none", padding: 0 },
  aviso: { color: "#6B4A00", background: "#FFF6E0", border: "1px solid #F0D9A0", padding: "10px 12px", borderRadius: 6 },
  ok: { color: "#1f7a33", background: "#E9F6EC", border: "1px solid #BFE0C5", padding: "10px 12px", borderRadius: 6 },
};

/**
 * Troca de e-mail em 2 passos. O e-mail da conta só muda DEPOIS de o código enviado ao
 * endereço NOVO ser confirmado; fechar o modal em qualquer ponto não altera nada.
 */
function AlterarEmailModal({ usuario, setUsuario, onClose }) {
  const [etapa, setEtapa] = useState("email"); // 'email' | 'codigo' | 'sucesso'
  const [novoEmail, setNovoEmail] = useState("");
  const [enviadoPara, setEnviadoPara] = useState("");
  const [codigo, setCodigo] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [mensagem, setMensagem] = useState(null); // { texto, tipo: 'erro' | 'aviso' | 'ok' }
  const [espera, setEspera] = useState(0);
  const [rodada, setRodada] = useState(0); // remonta o campo do código (devolve o foco ao 1º dígito)
  const emailRef = useRef(null);

  const controller = new ControllerConfiguracoes(usuario, {}, () => {}, setCarregando, setUsuario);

  useEffect(() => {
    if (etapa === "email" && emailRef.current) emailRef.current.focus();
  }, [etapa]);

  // contagem do reenvio
  useEffect(() => {
    if (etapa !== "codigo" || espera <= 0) return undefined;
    const t = setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [etapa, espera]);

  // Esc fecha
  useEffect(() => {
    const aoTeclar = (e) => { if (e.key === "Escape") fechar(); };
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etapa]);

  function fechar() {
    // Pedido em andamento é descartado; o e-mail da conta nunca foi tocado.
    if (etapa === "codigo") controller.cancelarTrocaEmail();
    onClose();
  }

  async function enviarCodigo(email, reenvio = false) {
    setMensagem(null);
    setCarregando(true);
    const r = await controller.solicitarTrocaEmail(email);
    setCarregando(false);
    if (!r.ok) {
      setMensagem({ texto: r.erro.mensagem, tipo: r.erro.brando ? "aviso" : "erro" });
      return false;
    }
    setEnviadoPara(email);
    setCodigo("");
    setRodada((n) => n + 1);
    setEspera(ESPERA_REENVIO);
    setEtapa("codigo");
    if (reenvio) setMensagem({ texto: "Enviamos um novo código. O anterior deixou de valer.", tipo: "ok" });
    return true;
  }

  function aoEnviar() {
    const email = novoEmail.trim().toLowerCase();
    if (!emailValido(email)) {
      setMensagem({ texto: "Digite um e-mail válido, como nome@exemplo.com.", tipo: "erro" });
      emailRef.current && emailRef.current.focus();
      return;
    }
    if (email === String(usuario.email || "").toLowerCase()) {
      setMensagem({ texto: "Esse já é o e-mail da sua conta. Digite o novo endereço que você quer usar.", tipo: "aviso" });
      return;
    }
    enviarCodigo(email);
  }

  async function aoConfirmar() {
    if (codigo.length < 6) {
      setMensagem({ texto: "Digite os 6 números do código.", tipo: "erro" });
      return;
    }
    setMensagem(null);
    setCarregando(true);
    const r = await controller.confirmarTrocaEmail(codigo);
    setCarregando(false);
    if (r.ok) { setEtapa("sucesso"); return; }
    const e = r.erro;
    setMensagem({ texto: e.mensagem, tipo: e.brando ? "aviso" : "erro" });
    if (e.codigo === "CODIGO_INCORRETO") { setCodigo(""); setRodada((n) => n + 1); }
  }

  const precisaNovoCodigo = mensagem && mensagem.tipo === "aviso" && etapa === "codigo";
  const gatilhoEnter = (acao) => (e) => { if (e.key === "Enter") { e.preventDefault(); acao(); } };

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="alterar-email-titulo">
      {carregando && <Loading />}
      {etapa === "sucesso" && (
        <AlertDialog titulo="E-mail alterado com sucesso!" buttonText="Ver meus dados" buttonOnClick={onClose} />
      )}
      <div className="content-modal">
        <div className="top">
          <h3 id="alterar-email-titulo">Alterar e-mail</h3>
          <button type="button" onClick={fechar} aria-label="Fechar sem alterar o e-mail">
            <img src={close_icon} alt="" />
          </button>
        </div>

        <Formulario id="form-alterar-email">
          {etapa !== "codigo" ?
            <div className="form-group">
              <span style={{ fontSize: 13, color: "#4D4F50", margin: "10px 0 4px" }}>
                E-mail atual: <strong>{usuario.email}</strong>
              </span>
              <label htmlFor="novo-email">Novo e-mail</label>
              <input
                ref={emailRef} type="email" id="novo-email" name="novo-email" autoComplete="email" inputMode="email"
                placeholder="Digite o novo e-mail" value={novoEmail}
                aria-describedby="novo-email-ajuda"
                onChange={(e) => { setNovoEmail(e.target.value); setMensagem(null); }}
                onKeyDown={gatilhoEnter(aoEnviar)}
                className={mensagem && mensagem.tipo === "erro" ? "input-error" : ""}
              />
              <span id="novo-email-ajuda" style={{ fontSize: 12, color: "#6B6E70", marginTop: 6 }}>
                Vamos enviar um código de 6 números para esse endereço, para confirmar que ele é seu.
                Seu e-mail atual continua valendo até você confirmar.
              </span>
            </div> :
            <div className="form-group" onKeyDown={gatilhoEnter(aoConfirmar)}>
              <span style={{ fontSize: 14, color: "#4D4F50", margin: "10px 0 2px" }}>
                Enviamos um código de 6 números para <strong>{enviadoPara}</strong>. Seu e-mail atual continua valendo até você confirmar.
              </span>
              <CodigoEmail key={rodada} valor={codigo} onChange={(v) => { setCodigo(v); if (mensagem && mensagem.tipo === "erro") setMensagem(null); }}
                invalido={!!mensagem && mensagem.tipo === "erro"} rotulo="Código enviado por e-mail" />
            </div>
          }

          <div className="form-group" aria-live="polite" style={{ minHeight: 18 }}>
            {mensagem &&
              <span
                role={mensagem.tipo === "erro" ? "alert" : "status"}
                style={{ fontSize: 13, lineHeight: "150%", ...CAIXA[mensagem.tipo] }}
              >
                {mensagem.texto}
              </span>
            }
          </div>

          <div className="form-group">
            {etapa !== "codigo" ?
              <button type="button" onClick={aoEnviar}>Enviar código</button> :
              <>
                <button type="button" onClick={precisaNovoCodigo ? () => enviarCodigo(enviadoPara, true) : aoConfirmar}>
                  {precisaNovoCodigo ? "Pedir novo código" : "Confirmar troca"}
                </button>
                <button
                  type="button" className="resend-code" disabled={espera > 0} style={SECUNDARIO}
                  onClick={() => enviarCodigo(enviadoPara, true)}
                >
                  {espera > 0 ? `Não recebi o código (reenviar em ${Math.floor(espera / 60)}:${String(espera % 60).padStart(2, "0")})` : "Não recebi o código: reenviar"}
                </button>
                <button
                  type="button" className="resend-code"
                  style={{ ...SECUNDARIO, background: "transparent", color: "var(--primary-color)", border: "none", textDecoration: "underline" }}
                  onClick={() => { setEtapa("email"); setMensagem(null); setCodigo(""); }}
                >
                  Usar outro e-mail
                </button>
              </>
            }
          </div>
        </Formulario>
      </div>
    </div>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(AlterarEmailModal);
